import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import style from './slider.css';
import paginationStyles from 'swiper/css/pagination?inline';
import navigationStyles from 'swiper/css/navigation?inline';
import scrollbarStyles from 'swiper/css/scrollbar?inline';
import { register } from 'swiper/element/bundle';

register();

/**
 * @property {number} slidesPerView
 * @property {number} spaceBetween
 * @property {'window' | 'container'} breakpointsBase
 * @property {object} breakpoints
 * @property {object} swiperOptions
 * @property {boolean} pagination
 * @property {boolean} navigation
 * @property {boolean} scrollbar
 */
export default class Slider extends TemplateElement {
    swiper;

    constructor() {
        super({ styles: [paginationStyles, navigationStyles, scrollbarStyles, style], shadowRender: true });
    }

    properties() {
        return {
            slideSelector: 'div',
            slidesPerView: 1.2,
            spaceBetween: 16,
            breakpointsBase: 'window',
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 40,
                },
            },
            swiperOptions: {},
            pagination: true,
            navigation: false,
            scrollbar: false,
        };
    }

    connected() {
        const swiperEl = this.$refs.swiper;
        const { on, ...swiperOptions } = this.swiperOptions ?? {};

        const swiperParams = {
            pagination: {
                el: this.$refs.pagination,
                type: 'bullets',
                clickable: true,
            },
            navigation: {
                prevEl: this.$refs.previousButton,
                nextEl: this.$refs.nextButton,
            },
            grabCursor: true,
            slidesPerView: this.slidesPerView,
            spaceBetween: this.spaceBetween,
            breakpointsBase: this.breakpointsBase,
            breakpoints: this.breakpoints,
            ...swiperOptions,
            on: {
                ...on,
                beforeInit: (...args) => {
                    this.setAttribute('swiper-connected', 'true');
                    on?.beforeInit?.(...args);
                },
            },
        };

        Object.assign(swiperEl, swiperParams);
        swiperEl.initialize();
    }

    events() {
        return {
            '[ref="swiper"]': {
                swiperslidechange: (event) => this.dispatch('mb-slide-change', event.detail[0].activeIndex),
            },
        };
    }

    template() {
        const slides = Array.from(Array(this.childElementCount || 0).keys());
        return html`
            <swiper-container
                ref="swiper"
                init="false"
                navigation="${this.navigation}"
                pagination="${this.pagination}"
                scrollbar="${this.scrollbar}"
            >
                ${slides.map((slide) => html` <swiper-slide><slot name="slide-${slide}"></slot></swiper-slide> `)}
            </swiper-container>
            ${when(this.pagination, html` <div ref="pagination" class="pagination"></div> `)}
            ${when(
                this.navigation,
                html`
                    <div ref="previousButton" class="navigation swiper-button-prev"></div>
                    <div ref="nextButton" class="navigation swiper-button-next"></div>
                `,
            )}
            ${when(this.scrollbar, html`<div class="swiper-scrollbar"></div>`)}
        `;
    }
}

defineElement('mb-slider', Slider);
